/* Loads GSAP once for every TONA section and runs the Osmo Supply effects "Highlight Text On Scroll",
   "Masked Text Reveal" (viewport mode) and "Tab System with Autoplay", a staggered reveal for cards, and the stats
   bars ported from the Aevum theme.
   Osmo cores are kept as shipped. The wrapper adds the same guards as the Aurum build: no animation in the
   theme editor or for reduced motion, no double split, and a re-run on shopify:section:load. */
(() => {
  if (window.TonaMotion) {
    window.TonaMotion.init(document);
    return;
  }

  const CDN = 'https://cdn.jsdelivr.net/npm/gsap@3.15.0/dist/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = Boolean(window.Shopify && window.Shopify.designMode);

  const load = (file) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CDN + file;
    script.async = false;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  let ready = null;
  const loadGsap = () => ready || (ready = (async () => {
    if (!window.gsap) await load('gsap.min.js');
    if (!window.ScrollTrigger) await load('ScrollTrigger.min.js');
    if (!window.SplitText) await load('SplitText.min.js');
    if (!window.CustomEase) await load('CustomEase.min.js');
    window.gsap.registerPlugin(window.ScrollTrigger, window.SplitText, window.CustomEase);
    CustomEase.create('osmo-ease', '0.625, 0.05, 0, 1');
    return window.gsap;
  })());

  // Osmo Supply: Highlight Text On Scroll
  function initHighlightText(scope) {
    let splitHeadingTargets = scope.querySelectorAll('[data-highlight-text]');
    splitHeadingTargets.forEach((heading) => {
      if (heading.dataset.highlightReady === 'true') return;
      heading.dataset.highlightReady = 'true';

      const scrollStart = heading.getAttribute('data-highlight-scroll-start') || 'top 90%';
      const scrollEnd = heading.getAttribute('data-highlight-scroll-end') || 'center 40%';
      const fadedValue = heading.getAttribute('data-highlight-fade') || 0.2; // start opacity
      const staggerValue = heading.getAttribute('data-highlight-stagger') || 0.1; // smoothness

      new SplitText(heading, {
        type: 'words, chars',
        autoSplit: true,
        onSplit(self) {
          let ctx = gsap.context(() => {
            let tl = gsap.timeline({
              scrollTrigger: { scrub: true, trigger: heading, start: scrollStart, end: scrollEnd }
            });
            tl.from(self.chars, { autoAlpha: fadedValue, stagger: staggerValue, ease: 'linear' });
          });
          return ctx; // returned so GSAP cleans up when onSplit fires again
        }
      });
    });
  }

  // Osmo Supply: Masked Text Reveal, viewport mode (Shopify variant from the Osmo note)
  function initViewportTextReveal(scope = document) {
    const elements = scope.querySelectorAll('[data-text-reveal]');
    const config = {
      lines: { duration: 0.8, stagger: 0.08 },
      words: { duration: 0.6, stagger: 0.06 },
      letters: { duration: 0.4, stagger: 0.008 }
    };
    const classMap = { lines: 'line', words: 'word', letters: 'letter' };

    elements.forEach((el) => {
      if (el.dataset.revealReady === 'true') return;
      el.dataset.revealReady = 'true';

      const type = el.dataset.revealType || 'lines';
      const { duration, stagger } = config[type];

      SplitText.create(el, { type: 'lines,words,chars', mask: 'lines', linesClass: 'line', wordsClass: 'word', charsClass: 'letter' });

      const targets = el.querySelectorAll(`.${classMap[type]}`);
      gsap.set(targets, { yPercent: 110 });

      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(targets, { yPercent: 0, duration, stagger, ease: 'osmo-ease' })
      });
    });
  }

  // TONA: cards in a [data-reveal-group] rise and fade in one after another, with the Osmo ease. Without marked
  // [data-reveal-item] children the group's own children rise, so shared snippets need no animation attributes.
  function initRevealGroups(scope = document) {
    scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
      if (group.dataset.revealReady === 'true') return;
      group.dataset.revealReady = 'true';
      const marked = group.querySelectorAll('[data-reveal-item]');
      const items = marked.length ? marked : group.children;
      if (!items.length) return;
      gsap.set(items, { autoAlpha: 0, y: 40 });
      ScrollTrigger.create({
        trigger: group,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(items, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'osmo-ease' })
      });
    });
  }

  // TONA: splits text into masked lines exactly where the browser already wrapped it. SplitText measures word by
  // word, which drops the kerning around spaces, so its lines broke differently from the finished text (the OEKO-TEX
  // text went from 4 to 5 lines) and words jumped a row when the split was undone. Each line here is one unbroken
  // run with its own markup, so the text sits in the same place before, during and after the reveal.
  function splitNaturalLines(el) {
    const original = el.innerHTML;
    const half = parseFloat(getComputedStyle(el).lineHeight) / 2 || 10;
    const range = document.createRange();
    const starts = [];
    let lastTop = null;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      for (let i = 0; i < node.data.length; i++) {
        if (/\s/.test(node.data[i])) continue;
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const top = range.getBoundingClientRect().top;
        if (lastTop === null || top > lastTop + half) {
          starts.push([node, i]);
          lastTop = top;
        }
      }
    }
    const fragments = starts.map(([node, offset], i) => {
      range.setStart(node, offset);
      if (starts[i + 1]) range.setEnd(starts[i + 1][0], starts[i + 1][1]);
      else range.setEnd(el, el.childNodes.length);
      return range.cloneContents();
    });
    el.replaceChildren(...fragments.map((fragment) => {
      const mask = document.createElement('div');
      const line = document.createElement('div');
      mask.className = 'line-mask';
      mask.style.overflow = 'hidden';
      mask.style.overflow = 'clip';
      line.className = 'line';
      line.style.whiteSpace = 'nowrap';
      line.append(fragment);
      mask.append(line);
      return mask;
    }));
    return { lines: [...el.querySelectorAll('.line')], revert: () => { el.innerHTML = original; } };
  }

  // Osmo Supply: Tab System with Autoplay. Kept as shipped: the progress bar is the timer, the isAnimating guard,
  // the first-run guard and the outgoing bar shrinking to the right.
  // Changed for TONA (Calvin, 25 Sep): the panels are text, so the old text slides up out of its line masks and the
  // new text rises line by line (Osmo Masked Text Reveal) instead of the fade and slide. There is no accordion detail.
  // Added from the Osmo note's gotchas: autoplay starts when the text scrolls into view, pauses while the logos are
  // hovered or keyboard focused, and arrow keys move between tabs. Without GSAP (editor, reduced motion, CDN blocked)
  // the tabs switch instantly and never rotate.
  function initTabs(scope, animateAll) {
    scope.querySelectorAll('[data-tabs="wrapper"]').forEach((wrapper) => {
      if (wrapper.dataset.tabsReady === 'true') return;
      wrapper.dataset.tabsReady = 'true';

      const contentItems = wrapper.querySelectorAll('[data-tabs="content-item"]');
      const visualItems = wrapper.querySelectorAll('[data-tabs="visual-item"]');
      if (!contentItems.length) return;

      // TONA: data-tabs-animate="false" keeps the tab behaviour without the line reveal, for panels that hold a
      // whole list (the FAQ) instead of one paragraph.
      const animate = animateAll && wrapper.dataset.tabsAnimate !== 'false';

      const autoplay = animate && wrapper.dataset.tabsAutoplay === 'true';
      const autoplayDuration = parseInt(wrapper.dataset.tabsAutoplayDuration) || 5000;

      let activeContent = null;
      let activeVisual = null;
      let isAnimating = false;
      let progressBarTween = null;
      let paused = false;

      // TONA: classes and ARIA flip at once, the animation plays on top.
      const setActive = (index) => contentItems.forEach((item, i) => {
        const on = i === index;
        item.classList.toggle('active', on);
        item.setAttribute('aria-selected', String(on));
        item.tabIndex = on ? 0 : -1;
        if (visualItems[i]) visualItems[i].classList.toggle('active', on);
      });
      const splitLines = (panel) => splitNaturalLines(panel.firstElementChild || panel);

      function startProgressBar(index) {
        if (progressBarTween) progressBarTween.kill();
        const bar = contentItems[index].querySelector('[data-tabs="item-progress"]');
        if (!bar) return;

        gsap.set(bar, { scaleX: 0, transformOrigin: 'left center' });
        progressBarTween = gsap.to(bar, {
          scaleX: 1,
          duration: autoplayDuration / 1000,
          ease: 'power1.inOut',
          onComplete: () => {
            if (!isAnimating) {
              const nextIndex = (index + 1) % contentItems.length;
              switchTab(nextIndex);
            }
          },
        });
        if (paused) progressBarTween.pause();
      }

      function switchTab(index) {
        if (isAnimating || contentItems[index] === activeContent) return;

        if (!animate) {
          setActive(index);
          activeContent = contentItems[index];
          activeVisual = visualItems[index];
          return;
        }

        isAnimating = true;
        if (progressBarTween) progressBarTween.kill();

        const outgoingContent = activeContent;
        const outgoingVisual = activeVisual;
        const outgoingBar = outgoingContent?.querySelector('[data-tabs="item-progress"]');

        const incomingContent = contentItems[index];
        const incomingVisual = visualItems[index];
        const incomingBar = incomingContent.querySelector('[data-tabs="item-progress"]');

        if (outgoingVisual) outgoingVisual.classList.add('is-leaving');
        setActive(index);

        const tl = gsap.timeline({
          defaults: { duration: 0.65, ease: 'power3' },
          onComplete: () => {
            activeContent = incomingContent;
            activeVisual = incomingVisual;
            isAnimating = false;
            if (autoplay) startProgressBar(index);
          },
        });

        if (outgoingContent) {
          const outgoingLines = splitLines(outgoingVisual);
          tl.set(outgoingBar, { transformOrigin: 'right center' })
            .to(outgoingBar, { scaleX: 0, duration: 0.3 }, 0)
            .to(outgoingLines.lines, { yPercent: -110, duration: 0.4, stagger: 0.03, ease: 'osmo-ease' }, 0)
            .add(() => {
              outgoingVisual.classList.remove('is-leaving');
              outgoingLines.revert();
            });
        }

        // The split is undone once the lines are in, so the text wraps freely again when the window resizes.
        const incomingLines = splitLines(incomingVisual);
        tl.fromTo(incomingLines.lines, { yPercent: 110 }, { yPercent: 0, duration: 0.8, stagger: 0.08, ease: 'osmo-ease' }, outgoingContent ? 0.3 : 0)
          .set(incomingBar, { scaleX: 0, transformOrigin: 'left center' }, 0)
          .add(() => incomingLines.revert());
      }

      contentItems.forEach((item, i) =>
        item.addEventListener('click', () => {
          if (item === activeContent) return;
          switchTab(i);
        })
      );

      // TONA: arrow keys, Home and End move between tabs (ARIA tabs pattern), missing in the Osmo original.
      wrapper.addEventListener('keydown', (event) => {
        const current = [...contentItems].indexOf(document.activeElement);
        const next = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: contentItems.length - 1 }[event.key];
        if (current < 0 || next === undefined) return;
        event.preventDefault();
        const target = contentItems[(next + contentItems.length) % contentItems.length];
        target.focus();
        target.click();
      });

      if (!animate) {
        activeContent = contentItems[0];
        activeVisual = visualItems[0];
        return;
      }

      if (autoplay) {
        // Only the logo row pauses the timer: the section fills the screen, so pausing on any hover would stop it for good.
        const tabList = contentItems[0].parentElement;
        let hovered = false;
        let focused = false;
        const sync = () => {
          paused = hovered || focused;
          if (progressBarTween) progressBarTween.paused(paused);
        };
        tabList.addEventListener('mouseenter', () => { hovered = true; sync(); });
        tabList.addEventListener('mouseleave', () => { hovered = false; sync(); });
        tabList.addEventListener('focusin', (event) => { focused = event.target.matches(':focus-visible'); sync(); });
        tabList.addEventListener('focusout', () => { focused = false; sync(); });
      }

      // Osmo note: start once the text is on screen, otherwise it has rotated a few times before anyone sees it.
      gsap.set(visualItems[0], { autoAlpha: 0 });
      ScrollTrigger.create({
        trigger: visualItems[0].parentElement,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.set(visualItems[0], { clearProps: 'opacity,visibility' });
          if (!activeContent) switchTab(0);
        },
      });
    });
  }

  // Stats bars, ported from the Aevum theme's avenir-stats-product.js (timings from its BYQ source). One timeline
  // for all bars, so the half-second rhythm survives fast scrolling. Bars grow by width, because scaleX squashes the
  // gradient. Each number counts up from the moment its text appears, keeping decimals and thousands separators.
  // The photo rises 50px. Nothing is hidden until .tona-stats--live is set here.
  const parseCount = (el) => {
    const m = /^(\D*?)([\d.,]+)(\D*)$/.exec((el.getAttribute('data-tona-stats-to') || '').trim());
    if (!m) return null;
    const raw = m[2].replace(/,/g, '');
    const target = parseFloat(raw);
    if (Number.isNaN(target)) return null;
    return { el, before: m[1], after: m[3], target, decimals: (raw.split('.')[1] || '').length, commas: m[2].includes(',') };
  };

  const writeCount = (c, value) => {
    let text = value.toFixed(c.decimals);
    if (c.commas) {
      const parts = text.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      text = parts.join('.');
    }
    c.el.textContent = c.before + text + c.after;
  };

  function initStats(scope) {
    scope.querySelectorAll('[data-tona-stats]').forEach((section) => {
      if (section.dataset.statsReady === 'true') return;
      section.dataset.statsReady = 'true';

      const tiles = [...section.querySelectorAll('.tona-stats__tile')];
      const media = section.querySelector('.tona-stats__media');
      section.classList.add('tona-stats--live');

      if (tiles.length) {
        // The bars sit well below the section top here (heading first), so the list starts the timeline.
        const tl = gsap.timeline({ scrollTrigger: { trigger: tiles[0].parentElement, start: 'top 85%', once: true } });
        tiles.forEach((tile, i) => {
          const start = i * 0.5;
          const fill = tile.querySelector('.tona-stats__fill');
          const text = tile.querySelector('.tona-stats__tile-text');
          const count = tile.querySelector('[data-tona-stats-to]');
          // TONA (Calvin, 25 Sep): the text waits until its bar is full. Aevum shows it halfway, and since the gradient
          // is still faint at that point the text seemed to come first.
          const textAt = fill ? start + 1 : start;
          if (fill) tl.fromTo(fill, { width: '0%' }, { width: '100%', duration: 1, ease: 'power2.inOut' }, start);
          if (text) tl.fromTo(text, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'none' }, textAt);
          const c = count && parseCount(count);
          if (!c) return;
          writeCount(c, 0); // the text is still at opacity 0, so the zero never shows as a flash
          const box = { v: 0 };
          tl.to(box, {
            v: c.target,
            duration: 1.6,
            ease: 'power2.out',
            onUpdate: () => writeCount(c, box.v),
            onComplete: () => writeCount(c, c.target),
          }, textAt);
        });
      }

      if (media) {
        gsap.fromTo(media, { opacity: 0, y: 50 }, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: { trigger: media, start: 'top 88%', once: true },
        });
      }
    });
  }

  // Everything is fully visible by default, so every fallback (editor, reduced motion, GSAP blocked) stays readable.
  const init = (scope) => {
    if (reduceMotion || inEditor) {
      initTabs(scope, false);
      return;
    }
    if (!scope.querySelector('[data-highlight-text], [data-text-reveal], [data-reveal-group], [data-tabs="wrapper"], [data-tona-stats]')) return;
    loadGsap()
      .then(() => document.fonts.ready)
      .then(() => {
        initHighlightText(scope);
        initViewportTextReveal(scope);
        initRevealGroups(scope);
        initTabs(scope, true);
        initStats(scope);
      })
      .catch(() => initTabs(scope, false));
  };

  window.TonaMotion = { init, loadGsap };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  // Theme editor: picking a block in the sidebar shows its tab. A block inside a panel (an FAQ question) opens the
  // tab that owns the panel, and a details block opens itself.
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest('[data-tabs="visual-item"]');
    const tab = event.target.closest('[data-tabs="content-item"]') || (panel && panel.id && document.querySelector(`[aria-controls="${panel.id}"]`));
    if (tab) tab.click();
    if (event.target.tagName === 'DETAILS') event.target.open = true;
  });
})();
